import dashboardAPI from './index'
import {
  MIN_BLOCK_DURATION_MS,
  runWithMinDuration,
} from '@/core/utils/requestPacing'
import { normalizeAverageDailySeries } from '@/core/utils/dailyAverageFields'

/** Meses que cubre cada petición del dashboard de insurance. */
export const INSURANCE_MONTHS_PER_BLOCK = 2

const DAILY_KEYS = ['total_daily', 'nc_daily', 'np_daily', 'fu_daily', 'rs_daily']

const pad = n => String(n).padStart(2, '0')
const toInt = v => parseInt(v, 10) || 0
const toNum = v => parseFloat(v) || 0

/**
 * Acepta `Y-m-d` o `m-d-Y` (con o sin ceros) y recuerda el formato
 * para que cada bloque se envíe igual que la fecha original.
 */
function parseDate(value) {
  const text = String(value).trim().split(' ')[0]
  const parts = text.split(/[-/]/).map(Number)
  if (parts[0] > 31) {
    return { year: parts[0], month: parts[1], day: parts[2], format: 'ymd' }
  }
  return { year: parts[2], month: parts[0], day: parts[1], format: 'mdy' }
}

function formatDate({ year, month, day }, format) {
  return format === 'mdy'
    ? `${pad(month)}-${pad(day)}-${year}`
    : `${year}-${pad(month)}-${pad(day)}`
}

function dateKey({ year, month, day }) {
  return year * 10000 + month * 100 + day
}

/** Días del rango igual que el backend: diferencia + 1, o 1 si es el mismo día. */
function backendDiffDays(start, end) {
  const s = Date.UTC(start.year, start.month - 1, start.day)
  const e = Date.UTC(end.year, end.month - 1, end.day)
  const diff = Math.round(Math.abs(e - s) / 86400000)
  return diff > 0 ? diff + 1 : 1
}

/**
 * Divide el rango en bloques de `monthsPerBlock` meses calendario,
 * recortando el primero y el último a las fechas solicitadas.
 */
export function generateBlocks(start, end, monthsPerBlock = INSURANCE_MONTHS_PER_BLOCK) {
  const s = parseDate(start)
  const e = parseDate(end)
  const blocks = []
  let year = s.year
  let month = s.month

  while (year * 100 + month <= e.year * 100 + e.month) {
    const lastMonthIndex = month - 1 + monthsPerBlock - 1
    const blockEndYear = year + Math.floor(lastMonthIndex / 12)
    const blockEndMonth = (lastMonthIndex % 12) + 1
    const blockEndDay = new Date(blockEndYear, blockEndMonth, 0).getDate()

    const from = blocks.length === 0 ? s : { year, month, day: 1 }
    let to = { year: blockEndYear, month: blockEndMonth, day: blockEndDay }
    if (dateKey(to) > dateKey(e)) to = e

    blocks.push({
      start: formatDate(from, s.format),
      end: formatDate(to, s.format),
      month: from.month,
      year: from.year,
    })

    const next = month - 1 + monthsPerBlock
    year += Math.floor(next / 12)
    month = (next % 12) + 1
  }

  return blocks
}

function sortByDateDesc(rows) {
  return rows.sort(
    (a, b) =>
      toInt(b.year) - toInt(a.year) ||
      toInt(b.month) - toInt(a.month) ||
      toInt(b.day) - toInt(a.day)
  )
}

/**
 * Cantidad de citas con total > 0 que el backend usó como divisor en `*_day`.
 * Sin ingresos no hay citas pagadas (el backend usa 1 solo para no dividir entre 0).
 */
function paidAppointments(block) {
  const incomeDay = toNum(block.average?.income_day)
  return incomeDay ? toNum(block.totals?.income) / incomeDay : 0
}

function appointmentCount(block, typeVisitPattern = null) {
  return (block.all_patients || [])
    .filter(p => !typeVisitPattern || typeVisitPattern.test(p.type_visit || ''))
    .reduce((sum, p) => sum + (toInt(p.count) || 1), 0)
}

/**
 * Promedio general ponderado por citas pagadas; si el promedio del bloque
 * coincide con ingresos / citas pagadas se usa el valor sin truncar.
 */
function averageTotal(blocks) {
  let weightSum = 0
  let valueSum = 0
  blocks.forEach(block => {
    const paid = paidAppointments(block)
    if (!paid) return
    const income = toNum(block.totals?.income)
    const blockAvg = toNum(block.average?.total)
    const exact = Math.trunc(income / paid) === Math.trunc(blockAvg) ? income / paid : blockAvg
    weightSum += paid
    valueSum += exact * paid
  })
  return weightSum > 0 ? Math.trunc(valueSum / weightSum) : 0
}

function weightedAverage(blocks, key, typeVisitPattern) {
  let weightSum = 0
  let valueSum = 0
  blocks.forEach(block => {
    const weight = appointmentCount(block, typeVisitPattern)
    weightSum += weight
    valueSum += toNum(block.average?.[key]) * weight
  })
  return weightSum > 0 ? Math.trunc(valueSum / weightSum) : 0
}

function mergeSummaryBucket(a = {}, b = {}) {
  const count = toInt(a.count) + toInt(b.count)
  const total = toNum(a.total) + toNum(b.total)
  return {
    count,
    total,
    average: count > 0 ? Math.round((total / count) * 100) / 100 : 0,
  }
}

/** Combina average.summary de todos los bloques (Σ total / Σ count). */
function combineAverageSummary(blocks) {
  const summary = {
    total: { average: 0, count: 0, total: 0 },
    np: { average: 0, count: 0, total: 0 },
    nc: { average: 0, count: 0, total: 0 },
  }
  let hasSummary = false
  blocks.forEach(block => {
    const src = block.average?.summary
    if (!src) return
    hasSummary = true
    ;['total', 'np', 'nc'].forEach(key => {
      summary[key] = mergeSummaryBucket(summary[key], src[key])
    })
  })
  return hasSummary ? summary : null
}

function averagesFromSummary(summary) {
  const truncBucket = key => Math.trunc(toNum(summary?.[key]?.average))
  return {
    total: truncBucket('total'),
    nc: truncBucket('nc'),
    np: truncBucket('np'),
  }
}

/** Suma patients_by_type[].count por type_visits_id. */
function mergePatientsByType(lists) {
  const map = new Map()
  lists.flat().forEach(item => {
    if (!item) return
    const key =
      item.type_visits_id != null
        ? `id:${item.type_visits_id}`
        : `p:${item.prefix || ''}`
    const prev = map.get(key)
    const count = toInt(item.count)
    if (!prev) {
      map.set(key, {
        type_visits_id: item.type_visits_id,
        prefix: item.prefix,
        name: item.name,
        count,
      })
    } else {
      prev.count += count
      if (!prev.prefix && item.prefix) prev.prefix = item.prefix
      if (!prev.name && item.name) prev.name = item.name
    }
  })
  return Array.from(map.values())
}

/** Combina average.by_type por type_visits_id (Σ total / Σ count). */
function mergeAverageByType(lists) {
  const map = new Map()
  lists.flat().forEach(item => {
    if (!item) return
    const key =
      item.type_visits_id != null
        ? `id:${item.type_visits_id}`
        : `p:${item.prefix || ''}`
    const prev = map.get(key)
    const count = toInt(item.count)
    const total = toNum(item.total)
    if (!prev) {
      map.set(key, {
        type_visits_id: item.type_visits_id,
        prefix: item.prefix,
        name: item.name,
        count,
        total,
        average: count > 0 ? Math.round((total / count) * 100) / 100 : 0,
      })
    } else {
      prev.count += count
      prev.total += total
      prev.average =
        prev.count > 0 ? Math.round((prev.total / prev.count) * 100) / 100 : 0
      if (!prev.prefix && item.prefix) prev.prefix = item.prefix
      if (!prev.name && item.name) prev.name = item.name
    }
  })
  return Array.from(map.values())
}

/**
 * Cada paciente aparece una sola vez; como el backend, su `type_membership`
 * es el de la cita más reciente (mayor `app_id`) del rango.
 */
function mergeByInsurance(blocks, allPatients) {
  const latestVisit = new Map()
  allPatients.forEach(p => {
    const current = latestVisit.get(p.id)
    if (!current || toInt(p.app_id) > toInt(current.app_id)) latestVisit.set(p.id, p)
  })

  const seenIds = new Set()
  const groups = new Map()
  ;[...blocks].reverse().forEach(block => {
    (block.all_patients_by_insurance || []).forEach(group => {
      const name = group.type_insurance || 'Sin definir'
      if (!groups.has(name)) groups.set(name, [])
      ;(group.patients || []).forEach(patient => {
        if (seenIds.has(patient.id)) return
        seenIds.add(patient.id)
        const latest = latestVisit.get(patient.id)
        groups
          .get(name)
          .push(latest ? { ...patient, type_membership: latest.type_membership } : patient)
      })
    })
  })
  return Array.from(groups.entries())
    .filter(([, patients]) => patients.length > 0)
    .map(([type_insurance, patients]) => ({
      type_insurance,
      total_patients: patients.length,
      patients,
    }))
}

function mergeYearData(blocks) {
  const yearData = blocks.flatMap(block => block.year_data || [])
  const facilityNames = []
  yearData.forEach(month =>
    (month.facilities || []).forEach(f => {
      if (!facilityNames.includes(f.facility)) facilityNames.push(f.facility)
    })
  )
  const template = (yearData.find(m => m.facilities?.length) || {}).facilities?.[0]
  if (!template) return yearData

  return yearData.map(month => {
    const facilities = [...(month.facilities || [])]
    facilityNames.forEach(name => {
      if (facilities.some(f => f.facility === name)) return
      const empty = Object.fromEntries(Object.keys(template).map(k => [k, 0]))
      facilities.push({ ...empty, facility: name })
    })
    return { ...month, facilities }
  })
}

function mergeInsBaseValue(blocks) {
  const result = { count: 0, total: 0, np: { count: 0, total: 0 }, nc: { count: 0, total: 0 } }
  blocks.forEach(({ ins_base_value: v }) => {
    if (!v) return
    result.count += toInt(v.count)
    result.total += toNum(v.total)
    result.np.count += toInt(v.np?.count)
    result.np.total += toNum(v.np?.total)
    result.nc.count += toInt(v.nc?.count)
    result.nc.total += toNum(v.nc?.total)
  })
  return result
}

/**
 * Combina las respuestas de `insurance/dashboard/get-data` por bloques
 * reproduciendo los cálculos que haría el backend con el rango completo.
 */
export function combineBlocks(blocks, { start, end }) {
  if (blocks.length === 0) return {}
  if (blocks.length === 1) return blocks[0]

  const diffDays = backendDiffDays(parseDate(start), parseDate(end))

  const income = blocks.reduce((s, b) => s + toNum(b.totals?.income), 0)
  const expenses = blocks.reduce((s, b) => s + toNum(b.totals?.expenses), 0)
  const refunds = blocks.reduce((s, b) => s + toNum(b.total_refunds), 0)
  const cash = blocks.reduce((s, b) => s + toNum(b.cash_cc?.cash), 0)
  const cc = blocks.reduce((s, b) => s + toNum(b.cash_cc?.cc), 0)

  const paid = Math.round(blocks.reduce((s, b) => s + paidAppointments(b), 0)) || 1

  const first = blocks[0]
  const firstPaid = Math.round(paidAppointments(first)) || 1
  const cashToday = toNum(first.average?.cash_day) * firstPaid
  const ccToday = toNum(first.average?.cc_day) * firstPaid

  const allPatients = blocks.flatMap(b => b.all_patients || [])

  const summary = combineAverageSummary(blocks)
  const fromSummary = summary ? averagesFromSummary(summary) : null
  const average = {
    income_days: income / diffDays,
    income_day: income / paid,
    expenses_days: expenses / diffDays,
    expenses_day: expenses / paid,
    net_income_days: (income - expenses - refunds) / diffDays,
    net_income_day: (income - expenses - refunds) / paid,
    total: fromSummary ? fromSummary.total : averageTotal(blocks),
    nc: fromSummary ? fromSummary.nc : weightedAverage(blocks, 'nc', /NC$/i),
    np: fromSummary ? fromSummary.np : weightedAverage(blocks, 'np', /NP$/i),
  }
  if (summary) average.summary = summary
  const np30 = blocks.reduce((s, b) => s + toInt(b.average?.np_30_count), 0)
  const nc30 = blocks.reduce((s, b) => s + toInt(b.average?.nc_30_count), 0)
  if (np30 || nc30 || blocks.some(b => b.average?.np_30_count != null)) {
    average.np_30_count = np30
    average.nc_30_count = nc30
  }
  DAILY_KEYS.forEach(key => {
    average[key] = sortByDateDesc(blocks.flatMap(b => b.average?.[key] || []))
  })
  // A9: alias AVG_CV (y tipadas) + remap legacy AVG_NP
  normalizeAverageDailySeries(average)
  const byType = mergeAverageByType(blocks.map(b => b.average?.by_type || []))
  if (byType.length || blocks.some(b => Array.isArray(b.average?.by_type))) {
    average.by_type = byType
  }
  average.nc_np = (average.nc + average.np) / 2
  average.cash_day = cashToday / paid
  average.cc_day = ccToday / paid
  average.cash_days = cash / diffDays
  average.cc_days = cc / diffDays

  const patientsByType = mergePatientsByType(blocks.map(b => b.patients_by_type || []))

  return {
    ...first,
    patients_per_month: blocks
      .flatMap(b => b.patients_per_month || [])
      .sort((a, b) => toInt(b.year) - toInt(a.year)),
    patients_per_day: sortByDateDesc(blocks.flatMap(b => b.patients_per_day || [])),
    patients_by_type: patientsByType,
    average,
    totals: { income, expenses, net_income: income - expenses - refunds },
    cash_cc: { cash, cc },
    total_refunds: refunds,
    all_patients: allPatients,
    all_patients_by_insurance: mergeByInsurance(blocks, allPatients),
    ins_base_value: mergeInsBaseValue(blocks),
    year_data: mergeYearData(blocks),
  }
}

/**
 * Carga el dashboard de insurance por bloques de meses.
 * @param {{ start: string, end: string, facilities: Array }} filter
 * @param {{ userId?: number|null, monthsPerBlock?: number, signal?: AbortSignal, onProgress?: Function }} options
 */
export async function loadProgressiveData(
  { start, end, facilities },
  { userId = null, monthsPerBlock = INSURANCE_MONTHS_PER_BLOCK, signal, onProgress } = {}
) {
  const blocks = generateBlocks(start, end, monthsPerBlock)
  const responses = []

  for (let i = 0; i < blocks.length; i++) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    const block = blocks[i]

    if (onProgress) {
      onProgress({
        current: i + 1,
        total: blocks.length,
        currentMonth: block.month,
        currentYear: block.year,
      })
    }

    const payload = { start_date: block.start, end_date: block.end, facilities }
    if (userId) payload.user_id = userId

    const isLast = i === blocks.length - 1
    const response = await runWithMinDuration(
      () => dashboardAPI.getData(payload, { signal }),
      isLast ? 0 : MIN_BLOCK_DURATION_MS
    )
    const data = response?.data ?? response
    if (!data || typeof data !== 'object' || !data.average) {
      throw new Error(`Invalid dashboard response for ${block.start} - ${block.end}`)
    }
    responses.push(data)
  }

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  return combineBlocks(responses, { start, end })
}

export default {
  generateBlocks,
  combineBlocks,
  loadProgressiveData,
}
